import React from 'react';
import { Box } from '@indeed/ifl-components';
import { Jobs } from '@indeed/ifl-icons';
import { AutoApplyHeader } from '../../../components/AutoApplyHeader/AutoApplyHeader';
import GettextFactory from '../../../util/GettextFactory';
import { MatchPreferencesSection } from '../components/MatchPreferencesSection';
import { ContactInformationSection } from './ContactInformationSection';
import {
    ResumePreviewSection,
    type ReviewIndeedResume,
    type ReviewSelectedFileResume,
} from './ResumePreviewSection';
import { ApplicationQuestionsSection } from './ApplicationQuestionsSection';
import type { ContactInformationData } from './contactInfo';

export interface CustomizeExperienceReviewProps {
    contactInfo: ContactInformationData;
    showIndeedResume: boolean;
    showFileResume: boolean;
    indeedResume: ReviewIndeedResume | undefined;
    selectedFileResume: ReviewSelectedFileResume | null;
    pdfUrl: string | null;
    loadingPdf: boolean;
    locale: string;
    onEditResume: () => void;
    /** Whether the SQ (screening question) prefill toggle is on. Default ON. */
    sqOptedIn: boolean;
    /** Updates the local SQ toggle state (persisted through updateAutoApply on Update). */
    onSqOptInChange: (checked: boolean) => void;
    /** Whether the DQ (voluntary self-identification) prefill toggle is on. Default OFF. */
    dqOptedIn: boolean;
    /** Updates the local DQ toggle state (persisted through setDqPrefillConsent on Update). */
    onDqOptInChange: (checked: boolean) => void;
    /** Whether the DQ experiment exposes its preference control. */
    showDqToggle: boolean;
}

/**
 * "Customize your experience" Review variant, shown when the
 * `rnhp_auto_apply_banner_tst` proctor is in the progressive-automation group
 * (value === 2). Renders the manage/edit content: Job preferences, Contact
 * information, Resume, and Application questions.
 *
 * Unlike the legacy Review page, every section is shown unconditionally (the
 * per-section proctors are bypassed in this variant). The SQ/DQ preference toggles
 * update local state only. When the user clicks Update, ReviewPage persists SQ through
 * `updateAutoApply` and DQ through the account-level `setDqPrefillConsent` mutation.
 *
 * Figma: https://www.figma.com/design/YqvUoqcp7hUVEjilAGM0ls/Auto-Apply-2026?node-id=19431-64136
 * JIRA: IAFE-2080
 *
 * Rendered inside ReviewPage's PageTransition/Helmet wrapper, alongside the
 * Update button.
 */
export const CustomizeExperienceReview: React.FC<CustomizeExperienceReviewProps> = ({
    contactInfo,
    showIndeedResume,
    showFileResume,
    indeedResume,
    selectedFileResume,
    pdfUrl,
    loadingPdf,
    locale,
    onEditResume,
    sqOptedIn,
    onSqOptInChange,
    dqOptedIn,
    onDqOptInChange,
    showDqToggle,
}) => {
    const gt = GettextFactory();

    // In this variant the Contact section is shown whenever contact data exists,
    // independent of the per-section `auto_apply_setup_review_contact_info` proctor.
    const hasContactData = !!(
        contactInfo.fullName ||
        contactInfo.phoneNumber ||
        contactInfo.country ||
        contactInfo.cityState ||
        contactInfo.address ||
        contactInfo.emailAddress
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <AutoApplyHeader
                title={gt.gettext('Customize your experience')}
                description={gt.gettext(
                    'Review and update your auto apply settings. You can change these anytime.',
                )}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {/* Job preferences */}
                <MatchPreferencesSection
                    title={gt.gettext('Job preferences')}
                    icon={<Jobs size="md" aria-hidden />}
                />

                {/* Contact information */}
                {hasContactData && <ContactInformationSection contactInfo={contactInfo} />}

                {/* Resume */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <ResumePreviewSection
                        heading={gt.gettext('Resume')}
                        onEditResume={onEditResume}
                        showIndeedResume={showIndeedResume}
                        showFileResume={showFileResume}
                        indeedResume={indeedResume}
                        selectedFileResume={selectedFileResume}
                        pdfUrl={pdfUrl}
                        loadingPdf={loadingPdf}
                        locale={locale}
                    />
                </Box>

                {/* Application questions */}
                <ApplicationQuestionsSection
                    sqOptedIn={sqOptedIn}
                    onSqOptInChange={onSqOptInChange}
                    dqOptedIn={dqOptedIn}
                    onDqOptInChange={onDqOptInChange}
                    showDqToggle={showDqToggle}
                />
            </Box>
        </Box>
    );
};
